/**
 * React hook for consensus analysis triggering and status polling
 * Phase 4 of Consensus Analysis Implementation
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { entitlementService } from '../../../services/entitlement';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import { auth } from '../../../lib/firebase/config';

interface AnalysisStatus {
  status: 'not_started' | 'processing' | 'completed' | 'failed';
  analysisAvailable: boolean;
  triggeredAt?: string;
  completedAt?: string;
  totalCostUsd?: number;
}

interface UseConsensusAnalysisReturn {
  status: AnalysisStatus | null;
  isLoading: boolean;
  error: string | null;
  checkStatus: () => Promise<void>;
  triggerAnalysis: () => Promise<void>;
  tokenQuotaExceeded: boolean;
  tokenQuotaInfo: {used: number, limit: number, planName: string} | null;
}

export function useConsensusAnalysis(comparisonId: string): UseConsensusAnalysisReturn {
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenQuotaExceeded, setTokenQuotaExceeded] = useState(false);
  const [tokenQuotaInfo, setTokenQuotaInfo] = useState<{used: number, limit: number, planName: string} | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialized = useRef(false);
  const tokenUsageEnabled = useFeatureFlag('TOKENUSAGE');

  const checkStatus = useCallback(async () => {
    console.log('[ConsensusAnalysis] Checking status:', {
      comparisonId,
      timestamp: new Date().toISOString()
    });

    try {
      setIsLoading(true);
      setError(null);

      const res = await fetch(`/api/consensus/${comparisonId}/status`);

      console.log('[ConsensusAnalysis] Status response:', {
        comparisonId,
        status: res.status,
        ok: res.ok,
        url: res.url
      });

      if (!res.ok) {
        console.error('[ConsensusAnalysis] Status check failed:', {
          comparisonId,
          status: res.status,
          statusText: res.statusText
        });
        throw new Error('Failed to check status');
      }

      const data = await res.json();

      console.log('[ConsensusAnalysis] Status data:', {
        comparisonId,
        status: data.status,
        analysisAvailable: data.analysisAvailable
      });

      setStatus(data);
    } catch (err) {
      console.error('[ConsensusAnalysis] Error checking status:', {
        comparisonId,
        error: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [comparisonId]);

  const startPolling = useCallback(() => {
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    console.log('[ConsensusAnalysis] Starting status polling');

    pollIntervalRef.current = setInterval(async () => {
      console.log('[ConsensusAnalysis] Polling status...');

      const statusRes = await fetch(`/api/consensus/${comparisonId}/status`);
      const statusData = await statusRes.json();

      console.log('[ConsensusAnalysis] Poll result:', {
        comparisonId,
        status: statusData.status
      });

      setStatus(statusData);

      if (statusData.status === 'completed' || statusData.status === 'failed') {
        console.log('[ConsensusAnalysis] Analysis finished:', {
          comparisonId,
          status: statusData.status,
          cost: statusData.totalCostUsd
        });

        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        setIsLoading(false);
      }
    }, 2000); // Poll every 2 seconds

    // Timeout after 3 minutes (longer for claim-level analysis)
    pollTimeoutRef.current = setTimeout(() => {
      console.warn('[ConsensusAnalysis] Polling timeout reached');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setIsLoading(false);
    }, 180000);
  }, [comparisonId]);

  const triggerAnalysis = useCallback(async () => {
    console.log('[ConsensusAnalysis] Triggering claim-level analysis:', {
      comparisonId,
      url: `/api/consensus/${comparisonId}/trigger-v2`,
      timestamp: new Date().toISOString()
    });

    try {
      setIsLoading(true);
      setError(null);
      setTokenQuotaExceeded(false);

      // Check token quota before triggering analysis (gated behind TOKENUSAGE flag)
      if (tokenUsageEnabled) {
        try {
          // First, verify we're authenticated
          const user = auth.currentUser;
          if (!user) {
            console.error('[ConsensusAnalysis] ❌ No authenticated user found');
            setError('Please sign in to use this feature.');
            setIsLoading(false);
            return;
          }

          console.log('[ConsensusAnalysis] Checking token quota before triggering analysis', {
            userEmail: user.email,
            userId: user.uid
          });

          const entitlementStatus = await entitlementService.getStatus();

          console.log('[ConsensusAnalysis] Entitlement status received:', {
            hasTokenQuota: !!entitlementStatus.quotas?.tokens,
            tokenQuota: entitlementStatus.quotas?.tokens
          });

          // CRITICAL FIX: Check if token quota is missing from response
          // This prevents bypass when backend errors cause missing quota data
          if (!entitlementStatus.quotas?.tokens) {
            console.error('[ConsensusAnalysis] ❌ Token quota missing from response', {
              hasQuotas: !!entitlementStatus.quotas,
              quotasKeys: entitlementStatus.quotas ? Object.keys(entitlementStatus.quotas) : [],
              isLoggedIn: !!user,
              userEmail: user?.email,
              paywallEnabled: entitlementStatus.paywallEnabled,
              planCode: entitlementStatus.plan?.code,
              timestamp: new Date().toISOString()
            });
            // Show quota modal with fail-closed values to block the action
            // This handles the case where server couldn't retrieve token quota
            setTokenQuotaInfo({
              used: 0,
              limit: 0,
              planName: entitlementStatus.plan?.displayName || 'Unknown'
            });
            setTokenQuotaExceeded(true);
            setIsLoading(false);
            return; // Block consensus analysis
          }

          // Check if token quota is exceeded
          if (!entitlementStatus.quotas.tokens.allowed) {
            console.log('[ConsensusAnalysis] ❌ TOKEN QUOTA EXCEEDED - BLOCKING', {
              used: entitlementStatus.quotas.tokens.used,
              limit: entitlementStatus.quotas.tokens.limit,
              allowed: entitlementStatus.quotas.tokens.allowed
            });
            setTokenQuotaInfo({
              used: entitlementStatus.quotas.tokens.used,
              limit: entitlementStatus.quotas.tokens.limit,
              planName: entitlementStatus.plan.displayName
            });
            setTokenQuotaExceeded(true);
            setIsLoading(false);
            return; // Block consensus analysis
          }

          console.log('[ConsensusAnalysis] ✅ Token quota check passed, proceeding with analysis', {
            used: entitlementStatus.quotas.tokens.used,
            limit: entitlementStatus.quotas.tokens.limit,
            remaining: entitlementStatus.quotas.tokens.remaining
          });
        } catch (quotaError) {
          console.error('[ConsensusAnalysis] CRITICAL ERROR: Failed to check token quota - BLOCKING:', quotaError);
          // CRITICAL FIX: Fail CLOSED - block analysis on errors
          setError('Unable to verify token quota. Please try again.');
          setIsLoading(false);
          return; // Block consensus analysis
        }
      }

      // Use trigger-v2 endpoint for claim-level analysis
      const res = await fetch(`/api/consensus/${comparisonId}/trigger-v2`, {
        method: 'POST'
      });

      console.log('[ConsensusAnalysis] Trigger response:', {
        comparisonId,
        status: res.status,
        ok: res.ok,
        url: res.url,
        statusText: res.statusText
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[ConsensusAnalysis] Trigger failed:', {
          comparisonId,
          status: res.status,
          statusText: res.statusText,
          error: data.error,
          fullResponse: data
        });
        throw new Error(data.error || `Request failed: ${res.status} ${res.statusText}`);
      }

      const triggerData = await res.json();
      console.log('[ConsensusAnalysis] Trigger successful:', {
        comparisonId,
        response: triggerData
      });

      // Check if already completed (cached)
      if (triggerData.alreadyCompleted) {
        console.log('[ConsensusAnalysis] Analysis already completed (cached)');
        await checkStatus();
        setIsLoading(false);
        return;
      }

      // Immediately check status after trigger
      // In production, analysis completes synchronously, so we need to fetch the updated status
      // without waiting for the polling interval (which would delay results by up to 2 seconds)
      console.log('[ConsensusAnalysis] Checking status immediately after trigger');
      await checkStatus();

      // Start polling for completion - first poll starts in 2 seconds, but we've already checked immediately above
      startPolling();

    } catch (err) {
      console.error('[ConsensusAnalysis] Error triggering analysis:', {
        comparisonId,
        error: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  }, [comparisonId, checkStatus, startPolling]);

  // Initialize: Check status on mount and resume polling if processing
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeStatus = async () => {
      console.log('[ConsensusAnalysis] Initializing status check on mount:', {
        comparisonId,
        timestamp: new Date().toISOString()
      });

      try {
        const res = await fetch(`/api/consensus/${comparisonId}/status`);

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
          console.error('[ConsensusAnalysis] Failed to fetch status on initialization:', {
            comparisonId,
            status: res.status,
            statusText: res.statusText,
            error: errorData.error,
            timestamp: new Date().toISOString()
          });
          // Set error state instead of silently failing
          setError(`Failed to load analysis status (HTTP ${res.status})`);
          return;
        }

        const data = await res.json();
        console.log('[ConsensusAnalysis] Existing status found:', {
          comparisonId,
          status: data.status,
          analysisAvailable: data.analysisAvailable,
          timestamp: new Date().toISOString()
        });

        setStatus(data);
        setError(null); // Clear any previous errors

        // Resume polling if analysis is still processing
        if (data.status === 'processing') {
          console.log('[ConsensusAnalysis] Resuming polling for in-progress analysis');
          setIsLoading(true);
          startPolling();
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[ConsensusAnalysis] Error during initialization:', {
          comparisonId,
          error: errorMessage,
          timestamp: new Date().toISOString()
        });
        setError(`Failed to initialize analysis: ${errorMessage}`);
      }
    };

    initializeStatus();
  }, [comparisonId, startPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  return { status, isLoading, error, checkStatus, triggerAnalysis, tokenQuotaExceeded, tokenQuotaInfo };
}
